import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Camera,
  X,
  Store,
  MapPin,
  FileText,
  Instagram,
  Globe,
  Palette,
  Truck,
  Package,
  Tag,
  Plus,
  Shield,
  CreditCard,
  Check,
  Loader2,
  Save,
  ChevronRight,
  AlertTriangle,
  Eye,
  EyeOff,
  Zap,
  ExternalLink,
  ShoppingBag,
  RefreshCw,
  Link2,
  Unlink,
  Bell,
  Sparkles,
  Users,
} from 'lucide-react';
import { doc, onSnapshot, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../config/firebase';
import { ensureBrowserCompatibleImage, resizeImageFile, IMAGE_LIMITS, padImageToAspectRatio } from '../../lib/imageUtils';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { AddressAutocomplete } from '../../components/ui/AddressAutocomplete';
import type {
  BusinessDocument,
  BusinessAddress,
  FulfillmentConfig,
  InventorySettings,
  LegalInfo,
  ClientSafeLegal,
  PrivateBusinessData,
  SocialMedia,
  PackagingType,
  BusinessType,
  ReturnPolicyType,
  StripeConnectionStatus,
  ShopifyConnection,
  DropoffType,
  UndeliverableAction,
} from '../../types/business';
import { BOUTIQUE_CATEGORIES, US_STATES, TAX_ID_SENTINEL } from '../../types/business';
import { ShopifyImport } from '../../components/business/ShopifyImport';
import { LocationManager } from '../../components/business/LocationManager';
import { PartnerInventoryPortal } from '../../components/business/PartnerInventoryPortal';
import { CatalogImport } from '../../components/business/CatalogImport';
import { PaymentSyncSettings } from '../../components/business/PaymentSyncSettings';
import { SeatsManager } from '../../components/business/team/SeatsManager';
import { usePayoutCompliance } from '../../hooks/usePayoutCompliance';

type SettingsSection =
  | 'identity'
  | 'address'
  | 'fulfillment'
  | 'catalog'
  | 'team'
  | 'legal'
  | 'social'
  | 'integrations';

interface SectionConfig {
  id: SettingsSection;
  label: string;
  icon: React.ElementType;
  description: string;
}

const SECTIONS: SectionConfig[] = [
  { id: 'identity', label: 'Brand & Images', icon: Camera, description: 'Logo, cover photo, name, and categories' },
  { id: 'address', label: 'Location', icon: MapPin, description: 'Store address and delivery area' },
  { id: 'fulfillment', label: 'Fulfillment', icon: Truck, description: 'Packaging and pickup details' },
  { id: 'catalog', label: 'Catalog', icon: Tag, description: 'Price range, collections, and stock alerts' },
  { id: 'team', label: 'Team & Seats', icon: Users, description: 'Invite employees and manage their access' },
  { id: 'legal', label: 'Legal & Payments', icon: Shield, description: 'Policies, Stripe, and business info' },
  { id: 'social', label: 'Social & Branding', icon: Palette, description: 'Social links and brand color' },
  { id: 'integrations', label: 'Integrations', icon: ShoppingBag, description: 'Connect Shopify and sync inventory' },
];

const PACKAGING_OPTIONS: Array<{ id: PackagingType; label: string; description: string }> = [
  { id: 'standard', label: 'Standard', description: 'Regular bags or boxes' },
  { id: 'fragile', label: 'Fragile', description: 'Extra care needed' },
  { id: 'garment_bag', label: 'Garment Bag', description: 'Hanging garments' },
  { id: 'gift_wrap', label: 'Gift Wrap', description: 'Gift-wrapped items' },
];

const BUSINESS_TYPES: Array<{ id: BusinessType; label: string }> = [
  { id: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { id: 'llc', label: 'LLC' },
  { id: 'corporation', label: 'Corporation' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'nonprofit', label: 'Non-Profit' },
];

const RETURN_POLICIES: Array<{ id: ReturnPolicyType; label: string; description: string }> = [
  { id: 'full_refund', label: 'Full Refund', description: 'Accept returns for a full refund within the window' },
  { id: 'exchange_only', label: 'Exchange Only', description: 'Accept returns for exchange or store credit only' },
  { id: 'store_credit', label: 'Store Credit', description: 'Returns receive store credit only' },
  { id: 'no_returns', label: 'All Sales Final', description: 'No returns or exchanges accepted' },
  { id: 'custom', label: 'Custom Policy', description: 'Write your own return policy' },
];

const RETURN_WINDOWS = [7, 14, 30, 60, 90];

const BRAND_COLORS = [
  '#C084FC', '#F472B6', '#FB923C', '#FBBF24', '#34D399',
  '#60A5FA', '#818CF8', '#E879F9', '#1F2937', '#EF4444',
];

// TODO: Re-enable when social media / branding features are ready for agencies
const SHOW_SOCIAL_SECTION = false;

export function BusinessSettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [business, setBusiness] = useState<Partial<BusinessDocument> | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<SettingsSection | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editable fields (local state mirrors Firestore, saved on demand)
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [logoPreview, setLogoPreview] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [featuredPreview, setFeaturedPreview] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [featuredFile, setFeaturedFile] = useState<File | null>(null);
  const [address, setAddress] = useState<BusinessAddress>({ street: '', unit: '', city: '', state: '', zipCode: '' });
  const [deliveryRadius, setDeliveryRadius] = useState(5);
  const [fulfillment, setFulfillment] = useState<FulfillmentConfig>({
    method: 'fash_drivers', packaging: ['standard'], maxOrdersPerHour: null, pickupInstructions: '',
    defaultDropoffType: 'DOOR', undeliverableAction: 'return', returnTripsEnabled: true,
    requiresGarmentBag: false, avgItemWeightGrams: null, requireSignature: false, requirePhotoProof: false,
  });
  const [inventorySettings, setInventorySettings] = useState<InventorySettings>({ trackInventory: true, lowStockThreshold: 5 });
  const [priceRange, setPriceRange] = useState({ min: 0, max: 500 });
  const [collections, setCollections] = useState<string[]>([]);
  const [legal, setLegal] = useState<LegalInfo>({
    businessName: '', dbaName: '', taxId: '', taxIdType: 'ein',
    businessType: 'sole_proprietorship',
    businessAddress: { street: '', city: '', state: '', zipCode: '' },
    stateOfIncorporation: '', dateOfFormation: '',
    authorizedRep: { fullName: '', title: '', email: '', phone: '' },
    w9Certification: { certified: false, certifiedAt: null, certifiedName: '' },
    returnPolicy: { type: 'full_refund', description: '', windowDays: 30 },
    storePolicy: { source: 'none', text: '', url: '' },
  });
  // Shown in place of the TIN, which the server never sends back.
  const [taxIdMasked, setTaxIdMasked] = useState('');
  const compliance = usePayoutCompliance();
  const [socialMedia, setSocialMedia] = useState<SocialMedia>({ instagram: '', tiktok: '', website: '' });
  const [brandColor, setBrandColor] = useState('#C084FC');

  // Stripe
  const [stripeStatus, setStripeStatus] = useState<StripeConnectionStatus | null>(null);
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeSuccess, setStripeSuccess] = useState<string | null>(null);

  // Stripe Connect (hosted onboarding — enables the platform fee-split)
  const [connectStarting, setConnectStarting] = useState(false);
  const [connectRefreshing, setConnectRefreshing] = useState(false);
  const [connectReturning, setConnectReturning] = useState(false);
  const [showManualKeys, setShowManualKeys] = useState(false);


  // Shopify
  const [shopifyStatus, setShopifyStatus] = useState<ShopifyConnection | null>(null);
  const [showShopifyImport, setShowShopifyImport] = useState(false);
  const [shopifyDisconnecting, setShopifyDisconnecting] = useState(false);
  const [shopifyShopDomain, setShopifyShopDomain] = useState('');
  const [shopifyConnecting, setShopifyConnecting] = useState(false);
  const [shopifyConnectError, setShopifyConnectError] = useState<string | null>(null);

  const [imageError, setImageError] = useState<string | null>(null);
  const [imageWarning, setImageWarning] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const featuredInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'identity') {
      setActiveSection('identity');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Partial<BusinessDocument>;
          setBusiness(data);
          setName(data.name || '');
          setDescription(data.description || '');
          setCategories(data.categories || []);
          setLogoPreview(data.logo || '');
          setCoverPreview(data.coverImage || '');
          setFeaturedPreview(data.featuredImage || '');
          setAddress(data.address || { street: '', unit: '', city: '', state: '', zipCode: '' });
          setDeliveryRadius(data.deliveryRadius || 5);
          setFulfillment({
            method: 'fash_drivers', packaging: ['standard'], maxOrdersPerHour: null, pickupInstructions: '',
            defaultDropoffType: 'DOOR', undeliverableAction: 'return', returnTripsEnabled: true,
            requiresGarmentBag: false, avgItemWeightGrams: null, requireSignature: false, requirePhotoProof: false,
            ...(data.fulfillment || {}),
          });
          setInventorySettings(data.inventorySettings || { trackInventory: true, lowStockThreshold: 5 });
          setPriceRange(data.priceRange || { min: 0, max: 500 });
          setCollections(data.collections || []);
          setSocialMedia(data.socialMedia || { instagram: '', tiktok: '', website: '' });
          setBrandColor(data.brandColor || '#C084FC');
          if (data.shopify) setShopifyStatus(data.shopify);
        }
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsubscribe();
  }, [user]);

  // Stripe connection state lives in the owner-only private subcollection
  // and changes from webhooks, so it is worth streaming.
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', user.uid, 'private', 'legal'),
      (snap) => {
        const priv = (snap.data() || {}) as Partial<PrivateBusinessData>;
        if (priv.stripe) setStripeStatus(priv.stripe);
      },
      (err) => console.error('Error loading private business data:', err)
    );
    return () => unsubscribe();
  }, [user]);

  // The tax record holds an EIN or SSN, which is encrypted at rest and never
  // sent to the browser. It therefore cannot be read from Firestore directly
  // — the callable returns everything else plus a masked TIN.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        const call = httpsCallable<Record<string, never>, ClientSafeLegal>(
          functions,
          'getBusinessTaxInfo',
        );
        const { data } = await call({});
        if (cancelled) return;
        const { taxIdMasked: masked, taxIdLast4: _last4, ...rest } = data;
        setLegal(rest);
        setTaxIdMasked(masked || '');
      } catch (err) {
        console.error('Error loading tax information:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  // Handle Shopify OAuth redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifyResult = params.get('shopify');
    if (shopifyResult === 'success') {
      const storeName = params.get('store');
      showToast('success', `Shopify connected${storeName ? `: ${storeName}` : ''}`);
      setActiveSection('integrations');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (shopifyResult === 'error') {
      const reason = params.get('reason') || 'Unknown error';
      showToast('error', `Shopify connection failed: ${reason}`);
      setActiveSection('integrations');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fn = httpsCallable(functions, 'getShopifyStatus');
    fn({}).then((res: any) => {
      if (!cancelled && res.data?.connected) {
        setShopifyStatus((prev) => ({ ...(prev || {} as any), ...res.data }));
      }
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [user]);

  // Handle return from Stripe Connect hosted onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Stripe's link expired and it bounced the user back to refresh_url.
    if (params.get('stripe_refresh') === '1') {
      setActiveSection('legal');
      showToast('error', 'That Stripe link expired. Tap “Continue on Stripe” to resume.');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (params.get('stripe_connect') !== 'success') return;
    const state = params.get('state') || undefined;
    setActiveSection('legal');
    setConnectReturning(true);
    // Strip the query params so a refresh doesn't re-trigger this.
    window.history.replaceState({}, '', window.location.pathname);
    (async () => {
      try {
        const handleReturn = httpsCallable(functions, 'handleStripeConnectReturn');
        const result = await handleReturn({ state });
        const status = result.data as StripeConnectionStatus;
        setStripeStatus(status);
        if (status.chargesEnabled) {
          showToast('success', 'Stripe account connected — you can now accept orders.');
        } else if (status.onboardingComplete) {
          showToast('success', 'Details submitted — Stripe is verifying your account.');
        } else {
          showToast('error', 'Stripe onboarding is incomplete. Please finish the remaining steps.');
        }
      } catch (err: any) {
        showToast('error', err?.message || 'Could not confirm Stripe onboarding.');
      } finally {
        setConnectReturning(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadImage = useCallback(async (file: File, path: string, kind: 'logo' | 'cover' | 'featured'): Promise<string> => {
    const compatible = await ensureBrowserCompatibleImage(file);
    const { maxWidth, maxHeight } = IMAGE_LIMITS[kind === 'featured' ? 'cover' : kind];
    const resized = await resizeImageFile(compatible, maxWidth, maxHeight);
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, resized);
    return getDownloadURL(snapshot.ref);
  }, []);

  const applyProcessedImage = (
    processedFile: File,
    type: 'logo' | 'cover' | 'featured',
    wasAdjusted: boolean,
  ) => {
    if (type === 'cover') {
      setCoverFile(processedFile);
    } else if (type === 'featured') {
      setFeaturedFile(processedFile);
    } else {
      setLogoFile(processedFile);
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'logo') setLogoPreview(reader.result as string);
      else if (type === 'cover') setCoverPreview(reader.result as string);
      else setFeaturedPreview(reader.result as string);
    };
    reader.readAsDataURL(processedFile);

    if (wasAdjusted) {
      setImageWarning(
        type === 'logo'
          ? "This isn't the optimized format for your logo — square (1:1) images work best. We've added padding so nothing gets stretched or cropped. You can continue with this version or upload a different image."
          : type === 'featured'
            ? "This isn't the optimized format for your homepage hero — a wide (~4.2:1) image works best. We've added padding so nothing gets stretched or cropped. You can continue with this version or upload a different image."
            : "This isn't the optimized format for your cover photo — a wide (~4.2:1) image works best. We've added padding so nothing gets stretched or cropped. You can continue with this version or upload a different image.",
      );
    } else {
      setImageWarning(null);
    }
  };

  const handleImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'logo' | 'cover' | 'featured'
  ) => {
    const raw = e.target.files?.[0];
    if (!raw || raw.size > 10 * 1024 * 1024) return;
    const isHeic = /\.(heic|heif)$/i.test(raw.name) || raw.type === 'image/heic' || raw.type === 'image/heif';
    if (!raw.type.startsWith('image/') && !isHeic) return;
    setImageError(null);
    setImageWarning(null);

    const limits = IMAGE_LIMITS[type === 'featured' ? 'cover' : type];

    try {
      const file = await ensureBrowserCompatibleImage(raw);
      const processed = await padImageToAspectRatio(file, limits.ratio, limits);
      applyProcessedImage(processed.file, type, processed.wasAdjusted);
    } catch {
      setImageError('Could not process this image format. Please use JPEG or PNG.');
    }

    e.target.value = '';
  };

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 3500);
  }, []);

  const handleSaveSection = async (section: SettingsSection) => {
    if (!user) {
      showToast('error', 'Sign in with a business account to save changes');
      return;
    }
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);

    try {
      let updates: Record<string, any> = { updatedAt: serverTimestamp() };
      let newLogo = logoPreview;
      let newCover = coverPreview;
      let newFeatured = featuredPreview;

      if (logoFile) {
        newLogo = await uploadImage(logoFile, `business-logos/${user.uid}/logo`, 'logo');
        setLogoFile(null);
        setLogoPreview(newLogo);
      }
      if (coverFile) {
        newCover = await uploadImage(coverFile, `business-logos/${user.uid}/cover`, 'cover');
        setCoverFile(null);
        setCoverPreview(newCover);
      }
      if (featuredFile) {
        newFeatured = await uploadImage(
          featuredFile,
          `business-logos/${user.uid}/featured-hero`,
          'featured',
        );
        setFeaturedFile(null);
        setFeaturedPreview(newFeatured);
      }

      switch (section) {
        case 'identity': {
          const identityUpdates: Record<string, unknown> = {
            ...updates,
            name: name.trim(),
            description: description.trim(),
            categories,
            logo: newLogo,
            coverImage: newCover,
          };
          if (business?.featured) {
            identityUpdates.featuredImage = newFeatured || null;
          }
          updates = identityUpdates;
          break;
        }
        case 'address': {
          const formattedAddress = [address.street, address.unit, address.city, address.state, address.zipCode]
            .filter(Boolean).join(', ');
          // Persist coordinates when Places Autocomplete captured them; when the
          // address was typed manually (no coords), null the location so the
          // checkout orchestrator re-geocodes it on the next order.
          const location =
            typeof address.lat === 'number' && typeof address.lng === 'number'
              ? { lat: address.lat, lng: address.lng }
              : null;
          updates = { ...updates, address, formattedAddress, deliveryRadius, location };
          break;
        }
        case 'fulfillment':
          updates = { ...updates, fulfillment };
          break;
        case 'catalog':
          updates = { ...updates, inventorySettings, priceRange, collections };
          break;
        case 'legal': {
          // The sentinel stands for a TIN already on file, so it is not a
          // value to validate — the server keeps the stored one.
          if (legal.taxId && legal.taxId !== TAX_ID_SENTINEL) {
            const digits = legal.taxId.replace(/\D/g, '');
            if (digits.length !== 9) {
              showToast('error', `${legal.taxIdType === 'ein' ? 'EIN' : 'SSN'} must be 9 digits`);
              setSaving(false);
              return;
            }
          }
          break;
        }
        case 'social':
          updates = { ...updates, socialMedia, brandColor };
          break;
      }

      if (section === 'legal') {
        // Routed through a callable rather than written directly: the TIN has
        // to be encrypted before it is stored, and an edit to any field the
        // W-9 attests to has to invalidate the existing signature. Neither is
        // something the client can be trusted to do.
        const save = httpsCallable<
          LegalInfo,
          { success: boolean; recertificationRequired: boolean }
        >(functions, 'saveBusinessTaxInfo');
        const { data: saveResult } = await save(legal);

        const refetch = httpsCallable<Record<string, never>, ClientSafeLegal>(
          functions,
          'getBusinessTaxInfo',
        );
        const { data: fresh } = await refetch({});
        const { taxIdMasked: masked, taxIdLast4: _last4, ...rest } = fresh;
        setLegal(rest);
        setTaxIdMasked(masked || '');
        void compliance.refresh();

        if (saveResult.recertificationRequired) {
          showToast(
            'error',
            'Your tax details changed, so your W-9 signature was cleared. Please certify again.',
          );
          setSaving(false);
          return;
        }
      } else {
        await updateDoc(doc(db, 'businesses', user.uid), updates);
      }
      setSaveSuccess(true);
      const sectionLabel = SECTIONS.find(s => s.id === section)?.label || 'Settings';
      showToast('success', `${sectionLabel} saved successfully`);
      setTimeout(() => setSaveSuccess(false), 2500);

      const productsQuery = query(
        collection(db, 'products'),
        where('brandId', '==', user.uid)
      );
      const productsSnap = await getDocs(productsQuery);
      if (productsSnap.empty) {
        setTimeout(() => {
          navigate('/business?tab=products');
        }, 1200);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setSaveError(true);
      showToast('error', 'Failed to save — please try again');
      setTimeout(() => setSaveError(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    const key = stripeApiKey.trim();
    const pubKey = publishableKey.trim();
    if (!key || !pubKey) {
      setStripeError('Both keys are required');
      return;
    }
    if (!key.startsWith('rk_') && !key.startsWith('sk_')) {
      setStripeError('Secret/restricted key must start with rk_live_, rk_test_, sk_live_, or sk_test_');
      return;
    }
    if (!pubKey.startsWith('pk_')) {
      setStripeError('Publishable key must start with pk_test_ or pk_live_');
      return;
    }
    setConnecting(true);
    setStripeError(null);
    setStripeSuccess(null);
    try {
      const connectStripe = httpsCallable(functions, 'connectStripeAccount');
      const result = await connectStripe({ secretKey: key, publishableKey: pubKey });
      const data = result.data as { success: boolean; mode: string; accountName: string };
      setStripeSuccess(`Connected to "${data.accountName}" (${data.mode} mode)`);
      setStripeApiKey('');
      setPublishableKey('');
    } catch (err: any) {
      setStripeError(err?.message || 'Failed to connect Stripe');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectStripe = async () => {
    setConnecting(true);
    setStripeError(null);
    try {
      const disconnectStripe = httpsCallable(functions, 'disconnectStripeAccount');
      await disconnectStripe({});
      setStripeSuccess('Stripe disconnected');
      setStripeStatus(null);
    } catch (err: any) {
      setStripeError(err?.message || 'Failed to disconnect');
    } finally {
      setConnecting(false);
    }
  };

  // ── Stripe Connect (hosted onboarding) ──────────────────────────────
  const handleStartConnectOnboarding = async () => {
    setConnectStarting(true);
    setStripeError(null);
    setStripeSuccess(null);
    try {
      const createLink = httpsCallable(functions, 'createStripeConnectLink');
      const result = await createLink({});
      const { url } = result.data as { url: string; accountId: string };
      if (!url) throw new Error('Stripe did not return an onboarding link');
      // Redirect the whole tab to Stripe's hosted onboarding.
      window.location.href = url;
    } catch (err: any) {
      setStripeError(err?.message || 'Could not start Stripe onboarding');
      setConnectStarting(false);
    }
  };

  const handleRefreshConnectStatus = async () => {
    setConnectRefreshing(true);
    setStripeError(null);
    try {
      const getStatus = httpsCallable(functions, 'getStripeConnectStatus');
      const result = await getStatus({});
      setStripeStatus(result.data as StripeConnectionStatus);
    } catch (err: any) {
      setStripeError(err?.message || 'Could not refresh Stripe status');
    } finally {
      setConnectRefreshing(false);
    }
  };

  const handleDisconnectConnect = async () => {
    setConnecting(true);
    setStripeError(null);
    setStripeSuccess(null);
    try {
      const disconnect = httpsCallable(functions, 'disconnectStripeConnect');
      await disconnect({});
      setStripeSuccess('Stripe account disconnected');
      setStripeStatus(null);
    } catch (err: any) {
      setStripeError(err?.message || 'Failed to disconnect');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectShopify = async () => {
    setShopifyDisconnecting(true);
    try {
      const disconnect = httpsCallable(functions, 'disconnectShopify');
      await disconnect({});
      setShopifyStatus(null);
      showToast('success', 'Shopify disconnected');
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to disconnect');
    } finally {
      setShopifyDisconnecting(false);
    }
  };

  const toggleCategory = (cat: string) => {
    if (categories.includes(cat)) {
      setCategories(categories.filter(c => c !== cat));
    } else if (categories.length < 5) {
      setCategories([...categories, cat]);
    }
  };

  const togglePackaging = (pkg: PackagingType) => {
    const current = fulfillment.packaging;
    if (current.includes(pkg)) {
      if (current.length > 1) setFulfillment({ ...fulfillment, packaging: current.filter(p => p !== pkg) });
    } else {
      setFulfillment({ ...fulfillment, packaging: [...current, pkg] });
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  // Stripe Connect (hosted onboarding) is the path that enables the platform
  // fee-split. It's "ready" only when Stripe has enabled charges on the account.
  const hasConnectAccount = !!stripeStatus?.connectAccountId;
  const connectReady = hasConnectAccount && stripeStatus?.chargesEnabled === true;
  const connectPending = hasConnectAccount && !connectReady;
  // Legacy manual-keys path (business pasted their own rk_/sk_ keys).
  const hasManualKeys = !hasConnectAccount && stripeStatus?.connected === true;
  const isConnected = connectReady || hasManualKeys;

  // Section list view
  if (!activeSection) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/business')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-serif text-heading">Settings</h1>
              <p className="text-xs text-body">{business?.name || 'Your Boutique'}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/boutique/${user?.uid}`, '_blank')}
            >
              <Eye className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Preview Store</span>
              <ExternalLink className="h-3 w-3 ml-1 sm:ml-1.5 opacity-50" />
            </Button>
            {business?.logo ? (
              <img src={business.logo} alt="" className="w-9 h-9 rounded-xl object-cover" />
            ) : (
              <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
                <Store className="h-4 w-4 text-white" />
              </div>
            )}
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {business?.featured && (
            <button
              onClick={() => setActiveSection('identity')}
              className="w-full rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 flex items-center gap-4 hover:border-amber-300 hover:shadow-sm transition-all text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading">Homepage Featured Store</p>
                <p className="text-xs text-body">
                  Your boutique is featured on the shopper homepage. Choose the hero image shoppers see first.
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-amber-500 flex-shrink-0" />
            </button>
          )}

          {SECTIONS.filter(s => SHOW_SOCIAL_SECTION || s.id !== 'social').map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className="w-full bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4 hover:border-primary/30 hover:shadow-sm transition-all text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <section.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading">{section.label}</p>
                <p className="text-xs text-body truncate">{section.description}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </button>
          ))}

          <div className="pt-4 space-y-3">
            <button
              onClick={() => navigate('/settings/notifications')}
              className="w-full bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4 hover:border-primary/30 hover:shadow-sm transition-all text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading">Notification Preferences</p>
                <p className="text-xs text-body">Enable push alerts for new orders and updates</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </button>

            <button
              onClick={() => navigate('/business/hours')}
              className="w-full bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4 hover:border-primary/30 hover:shadow-sm transition-all text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading">Store Hours</p>
                <p className="text-xs text-body">Weekly schedule, closures, and open/close toggle</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </button>
          </div>
        </main>

        {showShopifyImport && user && (
          <ShopifyImport
            businessId={user.uid}
            onClose={() => setShowShopifyImport(false)}
            onImportComplete={() => setShowShopifyImport(false)}
          />
        )}
      </div>
    );
  }

  const currentSection = SECTIONS.find(s => s.id === activeSection)!;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Save Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full px-4"
          >
            <div
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-2xl shadow-lg border backdrop-blur-sm',
                toastMessage.type === 'success'
                  ? 'bg-green-50/95 border-green-200 text-green-800'
                  : 'bg-red-50/95 border-red-200 text-red-800'
              )}
            >
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                  toastMessage.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                )}
              >
                {toastMessage.type === 'success' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                )}
              </div>
              <p className="text-sm font-medium flex-1">{toastMessage.text}</p>
              <button
                onClick={() => setToastMessage(null)}
                className={cn(
                  'p-1 rounded-full transition-colors flex-shrink-0',
                  toastMessage.type === 'success' ? 'hover:bg-green-100' : 'hover:bg-red-100'
                )}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Section Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setActiveSection(null)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-serif text-heading">{currentSection.label}</h1>
            <p className="text-xs text-body">{currentSection.description}</p>
          </div>
          <motion.div whileTap={{ scale: 0.95 }} className={cn(activeSection === 'team' && 'hidden')}>
            <Button
              size="sm"
              onClick={() => handleSaveSection(activeSection)}
              disabled={saving}
              className={cn(
                'transition-all duration-300 min-w-[90px]',
                saveSuccess && 'bg-green-600 hover:bg-green-700 border-green-600',
                saveError && 'bg-red-500 hover:bg-red-600 border-red-500'
              )}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : saveSuccess ? (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                  className="inline-flex"
                >
                  <Check className="h-4 w-4 mr-1.5" />
                </motion.span>
              ) : saveError ? (
                <AlertTriangle className="h-4 w-4 mr-1.5" />
              ) : (
                <Save className="h-4 w-4 mr-1.5" />
              )}
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : saveError ? 'Failed' : 'Save'}
            </Button>
          </motion.div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* ── IDENTITY SECTION ─── */}
        {activeSection === 'identity' && (
          <div className="space-y-6">
            {/* Cover Image */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <label className="text-sm font-medium text-heading">Cover Image</label>
                <p className="text-xs text-body">Recommended ~4.2:1 (e.g. 1600×380px) — other sizes are padded automatically</p>
                <div
                  onClick={() => coverInputRef.current?.click()}
                  className={cn(
                    'relative w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all overflow-hidden',
                    coverPreview ? 'border-transparent' : 'border-gray-200 hover:border-primary/40 bg-gray-50'
                  )}
                >
                  {coverPreview ? (
                    <>
                      <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full">Change Cover</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setCoverFile(null); setCoverPreview(''); }}
                        className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white"
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Camera className="h-8 w-8 text-gray-300 mb-2" />
                      <span className="text-sm text-body">Add a cover photo</span>
                    </div>
                  )}
                </div>
                <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageSelect(e, 'cover')} />
              </CardContent>
            </Card>

            {business?.featured && (
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-heading">Homepage Hero Image</label>
                      <p className="text-xs text-body mt-1">
                        This image rotates on the shopper homepage when your store is featured.
                        Leave blank to use your cover photo.
                      </p>
                    </div>
                  </div>

                  <div
                    onClick={() => featuredInputRef.current?.click()}
                    className={cn(
                      'relative w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all overflow-hidden',
                      (featuredPreview || coverPreview)
                        ? 'border-transparent'
                        : 'border-amber-200 hover:border-amber-300 bg-white'
                    )}
                  >
                    {(featuredPreview || coverPreview) ? (
                      <>
                        <img
                          src={featuredPreview || coverPreview}
                          alt="Homepage hero preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full">
                            {featuredPreview ? 'Change Hero Image' : 'Upload Hero Image'}
                          </span>
                        </div>
                        {featuredPreview && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFeaturedFile(null);
                              setFeaturedPreview('');
                            }}
                            className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white"
                          >
                            <X className="h-4 w-4 text-gray-600" />
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Camera className="h-8 w-8 text-amber-300 mb-2" />
                        <span className="text-sm text-body">Add a homepage hero image</span>
                      </div>
                    )}
                  </div>

                  {!featuredPreview && coverPreview && (
                    <p className="text-xs text-amber-800">
                      Preview shows your cover photo, which will be used until you upload a dedicated hero image.
                    </p>
                  )}

                  <input
                    ref={featuredInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageSelect(e, 'featured')}
                  />
                </CardContent>
              </Card>
            )}

            {/* Logo */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <label className="text-sm font-medium text-heading">Logo</label>
                <div className="flex items-center gap-4">
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    className={cn(
                      'relative w-20 h-20 rounded-full border-2 border-dashed cursor-pointer transition-all overflow-hidden flex-shrink-0',
                      logoPreview ? 'border-transparent' : 'border-gray-200 hover:border-primary/40 bg-gray-50'
                    )}
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Camera className="h-6 w-6 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <p className="text-xs text-body mt-1">PNG, JPG up to 10MB · Recommended square (1:1) — other sizes are padded automatically</p>
                  </div>
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageSelect(e, 'logo')} />
              </CardContent>
            </Card>

            {imageWarning && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">{imageWarning}</p>
              </div>
            )}

            {imageError && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{imageError}</p>
              </div>
            )}

            {/* Name */}
            <Card>
              <CardContent className="p-4 space-y-2">
                <label className="text-sm font-medium text-heading">Boutique Name</label>
                <Input
                  type="text"
                  placeholder="e.g., Maison de Luxe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                />
                <p className="text-xs text-body">{name.length}/60 characters</p>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardContent className="p-4 space-y-2">
                <label className="text-sm font-medium text-heading">Description</label>
                <textarea
                  placeholder="Tell shoppers what makes your boutique unique..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="flex w-full rounded-input border border-gray-200 bg-white px-3 py-3 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans resize-none"
                />
                <p className="text-xs text-body">{description.length}/500 characters</p>
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <label className="text-sm font-medium text-heading">
                  Categories <span className="text-body font-normal">(up to 5)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {BOUTIQUE_CATEGORIES.map((cat) => {
                    const isSelected = categories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-sm font-medium transition-all border',
                          isSelected
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-body border-gray-200 hover:border-primary/40 hover:text-heading'
                        )}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-body">{categories.length} of 5 selected</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── ADDRESS SECTION ─── */}
        {activeSection === 'address' && (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Search Address</label>
                  <AddressAutocomplete
                    value={address.street}
                    onChange={(val) => setAddress({ ...address, street: val })}
                    onSelect={(parsed) =>
                      setAddress({
                        ...address,
                        street: parsed.street,
                        city: parsed.city,
                        state: parsed.state,
                        zipCode: parsed.zipCode,
                        lat: parsed.lat,
                        lng: parsed.lng,
                      })
                    }
                    placeholder="Start typing an address..."
                  />
                  <p className="text-xs text-body">Select a suggestion or enter your address manually below</p>
                </div>

                <div className="relative flex items-center gap-3 py-1">
                  <div className="flex-1 border-t border-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">Address Details</span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Street Address</label>
                  <Input
                    type="text"
                    placeholder="123 Main St"
                    value={address.street}
                    onChange={(e) => setAddress({ ...address, street: e.target.value, lat: undefined, lng: undefined })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">
                    Unit / Suite <span className="text-body font-normal">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Suite 100"
                    value={address.unit}
                    onChange={(e) => setAddress({ ...address, unit: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-heading">City</label>
                    <Input
                      type="text"
                      placeholder="Los Angeles"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value, lat: undefined, lng: undefined })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-heading">State</label>
                    <select
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value, lat: undefined, lng: undefined })}
                      className="flex h-12 w-full rounded-input border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans"
                    >
                      <option value="">Select</option>
                      {US_STATES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">ZIP Code</label>
                  <Input
                    type="text"
                    placeholder="90001"
                    value={address.zipCode}
                    onChange={(e) => setAddress({ ...address, zipCode: e.target.value, lat: undefined, lng: undefined })}
                    maxLength={10}
                    className="w-40"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── FULFILLMENT SECTION ─── */}
        {activeSection === 'fulfillment' && (
          <div className="space-y-6">
            <div className="bg-purple-50/50 border border-primary/10 rounded-xl p-4 flex items-start gap-3">
              <Truck className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-heading">Deliveries powered by Uber Direct</p>
                <p className="text-xs text-body mt-1">
                  Couriers are automatically dispatched when you approve an order.
                </p>
              </div>
            </div>

            <Card>
              <CardContent className="p-4 space-y-4">
                <label className="text-sm font-medium text-heading">Packaging Types</label>
                <div className="grid grid-cols-2 gap-2">
                  {PACKAGING_OPTIONS.map((pkg) => {
                    const isSelected = fulfillment.packaging.includes(pkg.id);
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => togglePackaging(pkg.id)}
                        className={cn(
                          'flex flex-col p-3 rounded-xl border-2 transition-all text-left',
                          isSelected ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200'
                        )}
                      >
                        <span className="text-sm font-medium text-heading">{pkg.label}</span>
                        <span className="text-xs text-body">{pkg.description}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <label className="text-sm font-medium text-heading">
                  Max Orders Per Hour <span className="text-body font-normal">(optional)</span>
                </label>
                <p className="text-xs text-body">Leave empty for no limit.</p>
                <div className="w-40">
                  <Input
                    type="number"
                    placeholder="No limit"
                    value={fulfillment.maxOrdersPerHour ?? ''}
                    onChange={(e) => setFulfillment({ ...fulfillment, maxOrdersPerHour: e.target.value ? Number(e.target.value) : null })}
                    min={1}
                    max={100}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <label className="text-sm font-medium text-heading">
                  Store Pickup Instructions
                </label>
                <p className="text-xs text-body">
                  Saved on your store profile and sent to every Uber courier at pickup — e.g. mall floor,
                  suite number, or which entrance to use. (Max 280 characters.)
                </p>
                <textarea
                  placeholder="e.g., Inside the mall on the second floor next to Nordstrom"
                  value={fulfillment.pickupInstructions}
                  onChange={(e) => setFulfillment({ ...fulfillment, pickupInstructions: e.target.value.slice(0, 280) })}
                  rows={3}
                  maxLength={280}
                  className="flex w-full rounded-input border border-gray-200 bg-white px-3 py-3 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans resize-none"
                />
                <p className="text-xs text-body text-right">{fulfillment.pickupInstructions.length}/280</p>
              </CardContent>
            </Card>

            {/* Delivery Preferences */}
            <Card>
              <CardContent className="p-4 space-y-5">
                <div>
                  <label className="text-sm font-medium text-heading">Default Delivery Method</label>
                  <p className="text-xs text-body mb-2">How should the driver deliver by default? Customers can override this at checkout.</p>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { id: 'DOOR' as DropoffType, label: 'To the door', desc: 'Driver goes to unit' },
                      { id: 'LEAVE_AT_DOOR' as DropoffType, label: 'Leave at door', desc: 'Contactless drop-off' },
                      { id: 'CURBSIDE' as DropoffType, label: 'Curbside', desc: 'Customer meets outside' },
                    ]).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setFulfillment({ ...fulfillment, defaultDropoffType: opt.id })}
                        className={cn(
                          'flex flex-col p-3 rounded-xl border-2 transition-all text-left',
                          fulfillment.defaultDropoffType === opt.id ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200'
                        )}
                      >
                        <span className="text-sm font-medium text-heading">{opt.label}</span>
                        <span className="text-xs text-body">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-heading">If Delivery Fails</label>
                  <p className="text-xs text-body mb-2">What happens when the driver can&apos;t reach the customer?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { id: 'return' as UndeliverableAction, label: 'Return to store', desc: 'Driver brings item back (recommended for garments)' },
                      { id: 'leave_at_door' as UndeliverableAction, label: 'Leave at door', desc: 'Driver leaves at location (risk of theft)' },
                    ]).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setFulfillment({
                          ...fulfillment,
                          undeliverableAction: opt.id,
                          returnTripsEnabled: opt.id === 'return',
                        })}
                        className={cn(
                          'flex flex-col p-3 rounded-xl border-2 transition-all text-left',
                          fulfillment.undeliverableAction === opt.id ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200'
                        )}
                      >
                        <span className="text-sm font-medium text-heading">{opt.label}</span>
                        <span className="text-xs text-body">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Garment & Handling */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <label className="text-sm font-medium text-heading">Garment Handling &amp; Verification</label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fulfillment.requiresGarmentBag}
                    onChange={(e) => setFulfillment({ ...fulfillment, requiresGarmentBag: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-heading">Items on hangers / garment bags</span>
                    <p className="text-xs text-body">Tells the driver to keep packages upright to prevent wrinkling.</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fulfillment.requirePhotoProof}
                    onChange={(e) => setFulfillment({ ...fulfillment, requirePhotoProof: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-heading">Require photo proof of delivery</span>
                    <p className="text-xs text-body">Driver takes a photo when leaving the package. Helps with disputes.</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fulfillment.requireSignature}
                    onChange={(e) => setFulfillment({ ...fulfillment, requireSignature: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-heading">Require signature on delivery</span>
                    <p className="text-xs text-body">Customer must sign to receive. Best for high-value items.</p>
                  </div>
                </label>

                <div>
                  <label className="text-sm font-medium text-heading">
                    Average Item Weight <span className="text-body font-normal">(grams, optional)</span>
                  </label>
                  <p className="text-xs text-body mb-1">Helps the driver plan. A t-shirt is ~200g, a jacket ~800g. Leave empty for default (300g).</p>
                  <div className="w-40">
                    <Input
                      type="number"
                      placeholder="300"
                      value={fulfillment.avgItemWeightGrams ?? ''}
                      onChange={(e) => setFulfillment({ ...fulfillment, avgItemWeightGrams: e.target.value ? Number(e.target.value) : null })}
                      min={50}
                      max={5000}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── CATALOG SECTION ─── */}
        {activeSection === 'catalog' && (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-heading">Low Stock Alert</p>
                      <p className="text-xs text-body">Notify when a product drops below this quantity</p>
                    </div>
                  </div>
                  <div className="w-20">
                    <Input
                      type="number"
                      value={inventorySettings.lowStockThreshold}
                      onChange={(e) => setInventorySettings({ ...inventorySettings, lowStockThreshold: Math.max(1, Number(e.target.value)) })}
                      min={1}
                      max={100}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {user && <LocationManager businessId={user.uid} />}

            {user && <CatalogImport businessId={user.uid} />}

            <Card>
              <CardContent className="p-4 space-y-3">
                <label className="text-sm font-medium text-heading">Price Range</label>
                <p className="text-xs text-body">Helps shoppers filter boutiques.</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 space-y-1">
                    <label className="text-xs text-body">Minimum</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        type="number"
                        placeholder="0"
                        value={priceRange.min || ''}
                        onChange={(e) => setPriceRange({ ...priceRange, min: Number(e.target.value) })}
                        className="pl-7"
                        min={0}
                      />
                    </div>
                  </div>
                  <span className="text-body mt-5">&mdash;</span>
                  <div className="flex-1 space-y-1">
                    <label className="text-xs text-body">Maximum</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        type="number"
                        placeholder="500"
                        value={priceRange.max || ''}
                        onChange={(e) => setPriceRange({ ...priceRange, max: Number(e.target.value) })}
                        className="pl-7"
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        )}

        {/* ── TEAM & SEATS SECTION ─── */}
        {activeSection === 'team' && <SeatsManager businessId={user?.uid ?? null} />}

        {/* ── LEGAL SECTION ─── */}
        {activeSection === 'legal' && (
          <div className="space-y-6">

            {/* Completion status — server-computed, not inferred from the form */}
            {!compliance.loading && !compliance.payoutEligible && (
              <div
                className={cn(
                  'flex items-start gap-3 p-4 rounded-2xl border',
                  compliance.inGracePeriod
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-red-50 border-red-200',
                )}
              >
                <AlertTriangle
                  className={cn(
                    'h-5 w-5 flex-shrink-0 mt-0.5',
                    compliance.inGracePeriod ? 'text-amber-600' : 'text-red-600',
                  )}
                />
                <div className="min-w-0">
                  <p
                    className={cn(
                      'text-sm font-semibold',
                      compliance.inGracePeriod ? 'text-amber-800' : 'text-red-800',
                    )}
                  >
                    {compliance.inGracePeriod
                      ? compliance.graceDaysLeft !== null
                        ? `Tax information due in ${compliance.graceDaysLeft} day${compliance.graceDaysLeft === 1 ? '' : 's'}`
                        : 'Tax information incomplete'
                      : compliance.heldTotalCents > 0
                        ? `$${(compliance.heldTotalCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in payouts is on hold`
                        : 'Your payouts are on hold'}
                  </p>
                  <p
                    className={cn(
                      'text-xs mt-1',
                      compliance.inGracePeriod ? 'text-amber-700' : 'text-red-700',
                    )}
                  >
                    {compliance.inGracePeriod
                      ? 'Your payouts are arriving normally for now. Complete the fields below before the deadline to keep it that way.'
                      : compliance.heldCount > 0
                        ? `Held across ${compliance.heldCount} order${compliance.heldCount === 1 ? '' : 's'}. Everything is released automatically once the fields below are complete — nothing is forfeited.`
                        : 'FASH cannot pay out order proceeds until the fields below are complete.'}
                  </p>
                  {compliance.missing.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {compliance.missing.map((m) => (
                        <li
                          key={m.code}
                          className={cn(
                            'flex items-start gap-2 text-xs',
                            compliance.inGracePeriod ? 'text-amber-800' : 'text-red-800',
                          )}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              'mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full',
                              compliance.inGracePeriod ? 'bg-amber-500' : 'bg-red-500',
                            )}
                          />
                          <span>{m.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-heading">Fash Merchant Agreement</h3>
                    <p className="text-xs text-body mt-1">
                      Platform fees, fulfillment rules, payouts, and dispute terms that apply to your boutique.
                    </p>
                  </div>
                  <Link
                    to="/merchant-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-hover whitespace-nowrap"
                  >
                    View
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Card 1: Tax Information (W-9 Equivalent) */}
            <Card>
              <CardContent className="p-4 space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Tax Information
                  </h3>
                  <p className="text-xs text-body mt-1">Required for IRS 1099-K reporting. This information is encrypted and never shared with shoppers.</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Legal Business Name <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    placeholder="e.g., Maison de Luxe LLC"
                    value={legal.businessName}
                    onChange={(e) => setLegal({ ...legal, businessName: e.target.value })}
                  />
                  <p className="text-xs text-body">Exactly as registered with the IRS</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">DBA / Trade Name</label>
                  <Input
                    type="text"
                    placeholder="e.g., Maison Luxe Boutique"
                    value={legal.dbaName}
                    onChange={(e) => setLegal({ ...legal, dbaName: e.target.value })}
                  />
                  <p className="text-xs text-body">If different from your legal name (optional)</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Business Type <span className="text-red-500">*</span></label>
                  <select
                    value={legal.businessType}
                    onChange={(e) => {
                      const newType = e.target.value as BusinessType;
                      setLegal({
                        ...legal,
                        businessType: newType,
                        taxIdType: newType === 'sole_proprietorship' ? legal.taxIdType : 'ein',
                      });
                    }}
                    className="flex h-12 w-full rounded-input border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans"
                  >
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">
                    {legal.taxIdType === 'ssn' ? 'Social Security Number' : 'Employer Identification Number (EIN)'} <span className="text-red-500">*</span>
                  </label>
                  {legal.businessType === 'sole_proprietorship' && (
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setLegal({ ...legal, taxIdType: 'ein' })}
                        className={cn('px-3 py-1 rounded-full text-xs font-medium border transition-all', legal.taxIdType === 'ein' ? 'bg-primary text-white border-primary' : 'bg-white text-body border-gray-200')}
                      >
                        EIN
                      </button>
                      <button
                        type="button"
                        onClick={() => setLegal({ ...legal, taxIdType: 'ssn' })}
                        className={cn('px-3 py-1 rounded-full text-xs font-medium border transition-all', legal.taxIdType === 'ssn' ? 'bg-primary text-white border-primary' : 'bg-white text-body border-gray-200')}
                      >
                        SSN
                      </button>
                    </div>
                  )}
                  <Input
                    type="text"
                    placeholder={legal.taxIdType === 'ssn' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
                    value={legal.taxId === TAX_ID_SENTINEL ? taxIdMasked : legal.taxId}
                    onFocus={() => {
                      // Clear the mask so typing replaces it rather than
                      // appending to a value that is not really there.
                      if (legal.taxId === TAX_ID_SENTINEL) {
                        setLegal({ ...legal, taxId: '' });
                      }
                    }}
                    onBlur={() => {
                      // Left untouched — put the stored value back in play.
                      if (!legal.taxId && taxIdMasked) {
                        setLegal({ ...legal, taxId: TAX_ID_SENTINEL });
                      }
                    }}
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^\d]/g, '');
                      if (legal.taxIdType === 'ein') {
                        if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
                        if (val.length > 10) val = val.slice(0, 10);
                      } else {
                        if (val.length > 3) val = val.slice(0, 3) + '-' + val.slice(3);
                        if (val.length > 6) val = val.slice(0, 6) + '-' + val.slice(6);
                        if (val.length > 11) val = val.slice(0, 11);
                      }
                      setLegal({ ...legal, taxId: val });
                    }}
                    maxLength={legal.taxIdType === 'ssn' ? 11 : 10}
                  />
                  <p className="text-xs text-body">
                    {legal.taxId === TAX_ID_SENTINEL
                      ? 'Stored securely and encrypted. Tap the field to replace it.'
                      : legal.taxIdType === 'ssn'
                        ? 'Required for sole proprietors without an EIN. We recommend getting an EIN from the IRS (free).'
                        : 'Required for 1099-K tax reporting. Without this, 24% backup withholding applies to all payouts.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Business Address <span className="text-red-500">*</span></label>
                  <p className="text-xs text-body mb-2">Physical address only — PO Boxes are not accepted for tax filing (IRS W-9 requirement)</p>
                  <Input
                    type="text"
                    placeholder="Street address"
                    value={legal.businessAddress?.street || ''}
                    onChange={(e) => setLegal({ ...legal, businessAddress: { ...legal.businessAddress, street: e.target.value } })}
                  />
                  <div className="grid grid-cols-5 gap-2">
                    <Input
                      type="text"
                      placeholder="City"
                      value={legal.businessAddress?.city || ''}
                      onChange={(e) => setLegal({ ...legal, businessAddress: { ...legal.businessAddress, city: e.target.value } })}
                      className="col-span-2"
                    />
                    <select
                      value={legal.businessAddress?.state || ''}
                      onChange={(e) => setLegal({ ...legal, businessAddress: { ...legal.businessAddress, state: e.target.value } })}
                      className="flex h-12 w-full rounded-input border border-gray-200 bg-white px-2 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans col-span-1"
                    >
                      <option value="">State</option>
                      {US_STATES.map((s) => <option key={s.value} value={s.value}>{s.value}</option>)}
                    </select>
                    <Input
                      type="text"
                      placeholder="ZIP"
                      value={legal.businessAddress?.zipCode || ''}
                      onChange={(e) => setLegal({ ...legal, businessAddress: { ...legal.businessAddress, zipCode: e.target.value } })}
                      maxLength={10}
                      className="col-span-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-heading">State of Incorporation <span className="text-red-500">*</span></label>
                    <select
                      value={legal.stateOfIncorporation || ''}
                      onChange={(e) => setLegal({ ...legal, stateOfIncorporation: e.target.value })}
                      className="flex h-12 w-full rounded-input border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans"
                    >
                      <option value="">Select state</option>
                      {US_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-heading">Date of Formation</label>
                    <Input
                      type="date"
                      value={legal.dateOfFormation || ''}
                      onChange={(e) => setLegal({ ...legal, dateOfFormation: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: W-9 Certification + the representative who signs it */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    W-9 Certification
                  </h3>
                  <p className="text-xs text-body mt-1">Federal tax law requires this certification before we can process payouts.</p>
                </div>

                <div className="space-y-3 pb-4 border-b border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-heading flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Authorized representative
                    </p>
                    <p className="text-xs text-body mt-1">The person signing on behalf of the business. Your 1099-K goes to this contact.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-heading">Full Legal Name <span className="text-red-500">*</span></label>
                      <Input
                        type="text"
                        placeholder="Jane Smith"
                        value={legal.authorizedRep?.fullName || ''}
                        onChange={(e) => setLegal({ ...legal, authorizedRep: { ...legal.authorizedRep, fullName: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-heading">Title / Role <span className="text-red-500">*</span></label>
                      <Input
                        type="text"
                        placeholder="e.g., Owner, Managing Member"
                        value={legal.authorizedRep?.title || ''}
                        onChange={(e) => setLegal({ ...legal, authorizedRep: { ...legal.authorizedRep, title: e.target.value } })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-heading">Email for Tax Documents <span className="text-red-500">*</span></label>
                      <Input
                        type="email"
                        placeholder="jane@yourboutique.com"
                        value={legal.authorizedRep?.email || ''}
                        onChange={(e) => setLegal({ ...legal, authorizedRep: { ...legal.authorizedRep, email: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-heading">Phone Number <span className="text-red-500">*</span></label>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={legal.authorizedRep?.phone || ''}
                        onChange={(e) => setLegal({ ...legal, authorizedRep: { ...legal.authorizedRep, phone: e.target.value } })}
                      />
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-700 space-y-2">
                  <p className="font-medium text-gray-900">By certifying below, you confirm that:</p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-600">
                    <li>The taxpayer identification number (TIN) provided above is correct</li>
                    <li>You are not subject to backup withholding because: (a) you are exempt, (b) you have not been notified by the IRS that you are subject to backup withholding, or (c) you have been notified that you are no longer subject</li>
                    <li>You are a U.S. citizen or other U.S. person</li>
                    <li>The FATCA code(s) entered (if any) indicating exemption from FATCA reporting are correct</li>
                  </ol>
                </div>

                {legal.w9Certification?.certified ? (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Certified by {legal.w9Certification.certifiedName}</p>
                      <p className="text-xs text-green-600 mt-0.5">
                        {legal.w9Certification.certifiedAt ? new Date(legal.w9Certification.certifiedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Date recorded'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-heading">Type your full legal name to certify <span className="text-red-500">*</span></label>
                      <Input
                        type="text"
                        placeholder="Type your full legal name"
                        value={legal.w9Certification?.certifiedName || ''}
                        onChange={(e) => setLegal({ ...legal, w9Certification: { ...legal.w9Certification, certified: false, certifiedAt: null, certifiedName: e.target.value } })}
                        className="font-serif italic"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => {
                        if (legal.w9Certification?.certifiedName?.trim()) {
                          setLegal({
                            ...legal,
                            w9Certification: {
                              certified: true,
                              certifiedAt: new Date().toISOString(),
                              certifiedName: legal.w9Certification.certifiedName.trim(),
                            },
                          });
                        }
                      }}
                      disabled={!legal.w9Certification?.certifiedName?.trim()}
                      className="w-full"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      I certify under penalty of perjury that the above is correct
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Card 4: Fee Disclosure & Policies */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Fees & Policies
                  </h3>
                  <p className="text-xs text-body mt-1">Transparent breakdown of how FASH works for your business. No hidden fees.</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-50/60 border border-purple-100">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Tag className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-heading">Platform Commission: 18%</p>
                      <p className="text-xs text-body mt-0.5">Deducted from each sale. Covers marketplace operations, customer acquisition, and support.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-green-50/60 border border-green-100">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-heading">Payment Processing: Absorbed by FASH</p>
                      <p className="text-xs text-body mt-0.5">Stripe fees (2.9% + $0.30) come out of our commission, not yours.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50/60 border border-blue-100">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Truck className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-heading">Payout Schedule: Set by Stripe</p>
                      <p className="text-xs text-body mt-0.5">Your share of an order reaches your Stripe balance when the courier collects it. Stripe then pays your bank on the schedule set in your Stripe dashboard — typically every 2 business days. Payouts require complete tax information.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/60 border border-amber-100">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-heading">Sales Tax: Handled by FASH</p>
                      <p className="text-xs text-body mt-0.5">As a Marketplace Facilitator, FASH collects and remits sales tax on your behalf. You don't need to register or file for FASH orders.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-heading">Chargebacks</p>
                      <p className="text-xs text-body mt-0.5">FASH investigates all disputes. You are not charged unless fraud originated from your business (e.g., sending wrong item intentionally).</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-heading">1099-K Tax Reporting</p>
                      <p className="text-xs text-body mt-0.5">You&apos;ll receive a 1099-K by January 31 if your gross payments exceed $20,000 AND you have more than 200 transactions in the calendar year.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 5: Example Transaction */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    Example: How a $100 Sale Works
                  </h3>
                  <p className="text-xs text-body mt-1">Here&apos;s exactly what happens when a shopper buys a $100 item from your store.</p>
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                    <p className="text-xs font-medium text-body uppercase tracking-wide">Customer pays at checkout</p>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-body">Product price</span>
                      <span className="font-medium text-heading">$100.00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-body">Sales tax (collected by FASH)</span>
                      <span className="text-body">$8.49</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-body">Same-day delivery fee</span>
                      <span className="text-body">$9.99</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                      <span className="font-medium text-heading">Total charged to customer</span>
                      <span className="font-semibold text-heading">$118.48</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border-2 border-green-200 bg-green-50/30 overflow-hidden">
                  <div className="bg-green-50 px-4 py-2.5 border-b border-green-200">
                    <p className="text-xs font-medium text-green-800 uppercase tracking-wide">Your payout breakdown</p>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700">Your share (82%)</span>
                      <span className="font-semibold text-green-800">$82.00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-body">FASH commission (18%)</span>
                      <span className="text-body">-$18.00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-body">Processing fees</span>
                      <span className="text-green-600 font-medium">$0 (absorbed by FASH)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-body">Sales tax</span>
                      <span className="text-body italic">Remitted by FASH</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-body">Delivery</span>
                      <span className="text-body italic">Handled by FASH</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 mt-2 border-t border-green-200">
                      <span className="font-semibold text-green-900">You receive</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-green-800">$82.00</span>
                        <p className="text-xs text-green-600">Deposited in 2 business days</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stripe */}
            <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-purple-50/50 overflow-hidden">
              <div className="px-5 py-4 bg-white/60 border-b border-primary/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', connectReady ? 'bg-green-100' : connectPending ? 'bg-amber-100' : isConnected ? 'bg-green-100' : 'bg-primary/10')}>
                      {connectReady || isConnected ? <Check className="h-5 w-5 text-green-600" /> : connectPending ? <Loader2 className="h-5 w-5 text-amber-600" /> : <CreditCard className="h-5 w-5 text-primary" />}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-heading">Payment Processing</h3>
                      <p className="text-xs text-body">Powered by Stripe</p>
                    </div>
                  </div>
                  {connectReady ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Ready</span>
                  ) : connectPending ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Action needed</span>
                  ) : hasManualKeys ? (
                    <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', stripeStatus?.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>
                      {stripeStatus?.mode === 'live' ? 'Live (manual)' : 'Test (manual)'}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="p-5">
                {stripeError && (
                  <div className="flex items-start gap-2 p-3 mb-4 rounded-xl bg-red-50 border border-red-200">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{stripeError}</p>
                  </div>
                )}
                {stripeSuccess && (
                  <div className="flex items-start gap-2 p-3 mb-4 rounded-xl bg-green-50 border border-green-200">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-700">{stripeSuccess}</p>
                  </div>
                )}

                {connectReturning ? (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                    <Loader2 className="h-5 w-5 text-blue-500 animate-spin flex-shrink-0" />
                    <p className="text-sm text-blue-700">Confirming your Stripe account&hellip;</p>
                  </div>
                ) : connectReady ? (
                  /* ── State: Connect account ready (fee-split active) ── */
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">Ready to accept orders</p>
                        <p className="text-xs text-green-600 mt-0.5">{stripeStatus?.accountName || 'Your Stripe account'}{stripeStatus?.lastFour ? ` · payout ****${stripeStatus.lastFour}` : ''}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className={cn('flex items-center gap-2 p-2.5 rounded-lg border text-xs', stripeStatus?.chargesEnabled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500')}>
                        {stripeStatus?.chargesEnabled ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} Charges enabled
                      </div>
                      <div className={cn('flex items-center gap-2 p-2.5 rounded-lg border text-xs', stripeStatus?.payoutsEnabled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700')}>
                        {stripeStatus?.payoutsEnabled ? <Check className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5" />} Payouts {stripeStatus?.payoutsEnabled ? 'enabled' : 'pending'}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={handleRefreshConnectStatus} disabled={connectRefreshing}>
                        {connectRefreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        Refresh status
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleStartConnectOnboarding} disabled={connectStarting}>
                        {connectStarting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
                        Update on Stripe
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleDisconnectConnect} disabled={connecting} className="text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-500">
                        {connecting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Unlink className="h-3.5 w-3.5 mr-1.5" />}
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : connectPending ? (
                  /* ── State: Onboarding started but not finished ── */
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">Finish your Stripe setup</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          {stripeStatus?.onboardingComplete
                            ? 'Your details are submitted and Stripe is verifying your account. This can take a few minutes.'
                            : 'You started onboarding but haven\u2019t finished. Complete the remaining steps to start accepting orders.'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" onClick={handleStartConnectOnboarding} disabled={connectStarting}>
                        {connectStarting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening Stripe&hellip;</> : <><ExternalLink className="h-4 w-4 mr-2" />Continue on Stripe</>}
                      </Button>
                      <Button type="button" variant="outline" onClick={handleRefreshConnectStatus} disabled={connectRefreshing}>
                        {connectRefreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Refresh status
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ── State: Not connected — start Connect onboarding ── */
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50/80 border border-blue-100">
                      <Shield className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-700 space-y-1">
                        <p className="font-medium">Connect your bank to get paid</p>
                        <p className="text-blue-600">
                          Set up payments securely through Stripe. You&rsquo;ll be paid automatically for each
                          order &mdash; the platform fee and delivery cost are deducted for you, so you never
                          handle the split. Takes about 5 minutes.
                        </p>
                      </div>
                    </div>
                    <Button type="button" onClick={handleStartConnectOnboarding} disabled={connectStarting} className="w-full">
                      {connectStarting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening Stripe&hellip;</> : <><Zap className="h-4 w-4 mr-2" />Set up payments with Stripe</>}
                    </Button>

                    {hasManualKeys && (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <CreditCard className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-700">Using manual API keys{stripeStatus?.mode === 'live' ? ' (live)' : ' (test)'}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5">Switch to the button above to enable automatic payouts &amp; the fee-split.</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={handleDisconnectStripe} disabled={connecting} className="text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-500">
                          {connecting ? <Loader2 className="h-3.5 w-3.5" /> : 'Remove'}
                        </Button>
                      </div>
                    )}

                    {/* Advanced: manual API-key fallback */}
                    <div className="pt-1">
                      <button type="button" onClick={() => setShowManualKeys(v => !v)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                        <ChevronRight className={cn('h-3 w-3 transition-transform', showManualKeys && 'rotate-90')} />
                        Advanced: connect with API keys instead
                      </button>
                      {showManualKeys && (
                        <div className="mt-3 space-y-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                          <p className="text-[11px] text-gray-500">
                            Only use this if you can&rsquo;t complete Stripe onboarding. Manual keys don&rsquo;t support
                            automatic payouts or the platform fee-split.
                          </p>
                          <div className="space-y-1.5">
                            <label className="text-xs font-medium text-heading">Publishable Key</label>
                            <Input type="text" placeholder="pk_live_... or pk_test_..." value={publishableKey} onChange={(e) => { setPublishableKey(e.target.value); setStripeError(null); }} className="font-mono text-xs" autoComplete="off" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-medium text-heading">Secret / Restricted Key</label>
                            <div className="relative">
                              <Input type={showSecret ? 'text' : 'password'} placeholder="rk_live_... or sk_live_..." value={stripeApiKey} onChange={(e) => { setStripeApiKey(e.target.value); setStripeError(null); }} className="font-mono text-xs pr-10" autoComplete="off" />
                              <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <Button type="button" variant="outline" onClick={handleConnectStripe} disabled={connecting || !stripeApiKey.trim() || !publishableKey.trim()} className="w-full">
                            {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting&hellip;</> : 'Connect with keys'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Return Policy */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium text-heading">Return Policy</label>
                  <p className="text-xs text-body mt-0.5">Displayed to shoppers before purchase</p>
                </div>
                <div className="space-y-2">
                  {RETURN_POLICIES.map((policy) => {
                    const isSelected = legal.returnPolicy.type === policy.id;
                    return (
                      <button
                        key={policy.id}
                        type="button"
                        onClick={() => setLegal({ ...legal, returnPolicy: { ...legal.returnPolicy, type: policy.id } })}
                        className={cn(
                          'w-full flex items-center p-3 rounded-xl border-2 transition-all text-left',
                          isSelected ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200'
                        )}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-heading">{policy.label}</p>
                          <p className="text-xs text-body">{policy.description}</p>
                        </div>
                        <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0', isSelected ? 'border-primary' : 'border-gray-300')}>
                          {isSelected && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {legal.returnPolicy.type !== 'no_returns' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-heading">Return Window</label>
                    <div className="flex gap-2 flex-wrap">
                      {RETURN_WINDOWS.map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setLegal({ ...legal, returnPolicy: { ...legal.returnPolicy, windowDays: days } })}
                          className={cn(
                            'px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
                            legal.returnPolicy.windowDays === days
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white text-body border-gray-200 hover:border-primary/40'
                          )}
                        >
                          {days} days
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {legal.returnPolicy.type === 'custom' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-heading">Custom Policy Description</label>
                    <textarea
                      placeholder="Describe your return and exchange policy..."
                      value={legal.returnPolicy.description}
                      onChange={(e) => setLegal({ ...legal, returnPolicy: { ...legal.returnPolicy, description: e.target.value } })}
                      rows={4}
                      maxLength={500}
                      className="flex w-full rounded-input border border-gray-200 bg-white px-3 py-3 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans resize-none"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        )}

        {/* ── SOCIAL SECTION ─── */}
        {SHOW_SOCIAL_SECTION && activeSection === 'social' && (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-4 space-y-4">
                <label className="text-sm font-medium text-heading">Social Media</label>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs text-body">Instagram</label>
                    <div className="relative">
                      <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="@yourboutique"
                        value={socialMedia.instagram}
                        onChange={(e) => setSocialMedia({ ...socialMedia, instagram: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-body">TikTok</label>
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.2 8.2 0 004.76 1.52V6.8a4.85 4.85 0 01-1-.11z" />
                      </svg>
                      <Input
                        type="text"
                        placeholder="@yourboutique"
                        value={socialMedia.tiktok}
                        onChange={(e) => setSocialMedia({ ...socialMedia, tiktok: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-body">Website</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="url"
                        placeholder="https://yourboutique.com"
                        value={socialMedia.website}
                        onChange={(e) => setSocialMedia({ ...socialMedia, website: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium text-heading flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Brand Color
                  </label>
                  <p className="text-xs text-body mt-0.5">Accent color on your boutique page</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {BRAND_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setBrandColor(color)}
                      className={cn(
                        'w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center',
                        brandColor === color ? 'border-heading scale-110 shadow-md' : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color }}
                    >
                      {brandColor === color && <div className="w-3 h-3 bg-white rounded-full shadow-sm" />}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border-2 border-gray-200 flex-shrink-0" style={{ backgroundColor: brandColor }} />
                  <Input
                    type="text"
                    placeholder="#C084FC"
                    value={brandColor}
                    onChange={(e) => { if (e.target.value.match(/^#[0-9A-Fa-f]{0,6}$/)) setBrandColor(e.target.value); }}
                    maxLength={7}
                    className="w-32 font-mono"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardContent className="p-4 space-y-2">
                <label className="text-sm font-medium text-heading">Preview</label>
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <div className="h-20 w-full" style={{ backgroundColor: brandColor + '20' }} />
                  <div className="p-4 -mt-6">
                    <div className="flex items-end gap-3">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-12 h-12 rounded-full border-2 border-white object-cover shadow-sm" />
                      ) : (
                        <div className="w-12 h-12 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white font-serif text-lg" style={{ backgroundColor: brandColor }}>
                          {name?.[0]?.toUpperCase() || 'B'}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-heading">{name || 'Your Boutique'}</p>
                        <p className="text-xs text-body">{categories.slice(0, 2).join(' · ') || 'Fashion'}</p>
                      </div>
                    </div>
                    {(socialMedia.instagram || socialMedia.website) && (
                      <div className="flex gap-3 mt-3 text-xs text-body">
                        {socialMedia.instagram && <span>{socialMedia.instagram}</span>}
                        {socialMedia.website && <span className="truncate">{socialMedia.website}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* ── INTEGRATIONS SECTION ─── */}
        {activeSection === 'integrations' && (
          <div className="space-y-6">
            {user && <PaymentSyncSettings businessId={user.uid} />}
            {user && <PartnerInventoryPortal businessId={user.uid} />}
            <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-green-50/50 overflow-hidden">
              <div className="px-5 py-4 bg-white/60 border-b border-primary/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center',
                      shopifyStatus?.connected ? 'bg-green-100' : 'bg-primary/10'
                    )}>
                      {shopifyStatus?.connected ? (
                        <Check className="h-5 w-5 text-green-600" />
                      ) : (
                        <ShoppingBag className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-heading">Shopify</h3>
                      <p className="text-xs text-body">Import products &amp; sync inventory</p>
                    </div>
                  </div>
                  {shopifyStatus?.connected && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" />
                      Syncing
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                {shopifyStatus?.connected ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200">
                      <Link2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">Connected</p>
                        <p className="text-xs text-green-600 mt-0.5">
                          {shopifyStatus.storeName || shopifyStatus.shopDomain}
                        </p>
                      </div>
                    </div>
                    {shopifyStatus.lastSyncAt && (
                      <p className="text-xs text-body">
                        Last synced: {new Date(shopifyStatus.lastSyncAt as any).toLocaleString()}
                      </p>
                    )}
                    {shopifyStatus.needsReconsent && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                        Reconnect Shopify to grant location access. Multi-location sync is paused until you reconnect.
                      </p>
                    )}
                    <div className="flex items-center gap-3">
                      <Button type="button" size="sm" onClick={() => setShowShopifyImport(true)} className="flex-1">
                        <ShoppingBag className="h-3.5 w-3.5 mr-1.5" />
                        Import Products
                      </Button>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={handleDisconnectShopify}
                        disabled={shopifyDisconnecting}
                        className="text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-500"
                      >
                        {shopifyDisconnecting ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Unlink className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-body">
                      Connect your Shopify store to import products and keep inventory in sync across both platforms.
                    </p>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-heading">Your store website or domain</label>
                        <Input
                          type="text"
                          placeholder="yourstore.com or your-store.myshopify.com"
                          value={shopifyShopDomain}
                          onChange={(e) => {
                            setShopifyShopDomain(e.target.value);
                            setShopifyConnectError(null);
                          }}
                          className="font-mono text-sm"
                          autoComplete="off"
                        />
                        <p className="text-xs text-body">
                          Paste your store website (e.g. <span className="font-mono">yourstore.com</span>) — we&apos;ll find your
                          Shopify store automatically. Your <span className="font-mono">.myshopify.com</span> address works too.
                        </p>
                      </div>
                      {shopifyConnectError && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-red-700">{shopifyConnectError}</p>
                        </div>
                      )}
                      <Button
                        type="button"
                        disabled={!shopifyShopDomain.trim() || shopifyConnecting}
                        onClick={async () => {
                          if (!user || !shopifyShopDomain.trim()) return;
                          setShopifyConnecting(true);
                          setShopifyConnectError(null);
                          try {
                            const startOAuth = httpsCallable(functions, 'shopifyAuthStart');
                            const result = await startOAuth({ shop: shopifyShopDomain.trim() });
                            const { authUrl } = result.data as { authUrl: string };
                            window.location.href = authUrl;
                          } catch (err: any) {
                            console.error('Shopify connect failed:', err);
                            setShopifyConnecting(false);
                            setShopifyConnectError(err?.message || 'Could not find your Shopify store. Try your .myshopify.com address.');
                          }
                        }}
                        className="w-full"
                      >
                        {shopifyConnecting ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Link2 className="h-4 w-4 mr-2" />
                        )}
                        {shopifyConnecting ? 'Finding your store...' : 'Connect with Shopify'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-amber-50/50 border border-amber-200/50 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Inventory Sync</p>
                <p className="text-xs text-amber-700 mt-1">
                  When connected, inventory changes on Shopify are automatically reflected here in real-time.
                  Sales on FASH will also update your Shopify stock to prevent overselling.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {showShopifyImport && user && (
        <ShopifyImport
          businessId={user.uid}
          onClose={() => setShowShopifyImport(false)}
          onImportComplete={() => setShowShopifyImport(false)}
        />
      )}
    </div>
  );
}
